import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import recurrenceOptions from '../../data/recurrenceOptions';
import petVaccines from '../../data/petVaccines';
import { useTranslation } from 'react-i18next'; // Import translation hook
import i18n from '../../i18n'; // Import i18n for language detection
import '../../style/AddVaccinePage.css'; // Import the CSS file

const AddVaccinePage = () => {
    const { id } = useParams(); // Get pet ID from URL
    const navigate = useNavigate(); // For redirection
    const { t } = useTranslation(); // Translation function
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000'; // Backend URL
    const [newVaccine, setNewVaccine] = useState({
        name: [],
        otherName: '',
        lastGivenDate: '',
        recurrence: '',
        endDate: '',
    });
    const [error, setError] = useState(null);

    const handleVaccineChange = (selectedOptions) => {
        const selectedValues = selectedOptions.map((option) => option.value);
        setNewVaccine({
            ...newVaccine,
            name: selectedValues,
        });
    };

    const handleAddVaccine = async () => {
        try {
            const storedUser = JSON.parse(localStorage.getItem('user')); // Retrieve user data from localStorage
            const token = storedUser?.token;

            if (!token) {
                throw new Error(t('general.noAuthToken')); // Use translation for error
            }

            // Include "Other" vaccine name if provided
            const formattedOtherName = newVaccine.otherName.trim().replace(/\s+/g, '_').toLowerCase();
            const vaccineNames = newVaccine.name.filter((name) => name !== 'other'); // Exclude "other"
            if (formattedOtherName) {
                vaccineNames.push(formattedOtherName); // Add formatted "Other" name
            }

            // Create the vaccine
            const vaccineResponse = await fetch(`${baseUrl}/api/pets/${id}/vaccines`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`, // Include token in Authorization header
                },
                body: JSON.stringify({ ...newVaccine, name: vaccineNames }),
            });

            if (!vaccineResponse.ok) {
                throw new Error(t('vaccine.createFailed')); // Use translation for error
            }

            const addedVaccine = await vaccineResponse.json(); // Expect the backend to return only the newly created vaccine
            console.log(addedVaccine); // Debug log

            if (!addedVaccine._id) {
                throw new Error(t('vaccine.missingVaccineId')); // Use translation for error
            }

            navigate(`/pet/${id}/vaccine/${addedVaccine._id}`); // Redirect to the Vaccine Details Page
        } catch (error) {
            setError(error.message);
            console.error(error);
        }
    };

    return (
        <div className="add-vaccine-form bg-dafauna-background rounded-xl shadow-md p-8">
            <h3 className="text-dafauna-darkText text-2xl font-bold text-center mb-4">{t('vaccine.title')}</h3>
            {error && <p className="error-message text-red-500 text-center">{error}</p>}
            <div className="form-group">
                <label htmlFor="vaccineName" className="block text-dafauna-darkText font-medium mb-2">
                    {t('vaccine.vaccineName')}
                </label>
                <Select
                    id="vaccineName"
                    name="vaccineName"
                    isMulti // Enable multi-select
                    options={[
                        ...petVaccines.map((vaccine) => ({
                            value: vaccine.value,
                            label: vaccine.label[i18n.language], // Use multilingual labels
                        })),
                        { value: 'other', label: t('vaccine.otherOption') }, // Add "Other" option
                    ]}
                    value={newVaccine.name.map((name) => {
                        const vaccineData = petVaccines.find((v) => v.value === name);
                        return {
                            value: name,
                            label: vaccineData ? vaccineData.label[i18n.language] : t('vaccine.otherOption'),
                        };
                    })}
                    onChange={handleVaccineChange}
                    placeholder={t('vaccine.vaccinePlaceholder')}
                    className="form-select border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                />
            </div>
            {newVaccine.name.includes('other') && ( // Show input field if "Other" is selected
                <div className="form-group">
                    <label htmlFor="otherName" className="block text-dafauna-darkText font-medium mb-2">
                        {t('vaccine.otherName')}
                    </label>
                    <input
                        type="text"
                        id="otherName"
                        placeholder={t('vaccine.otherNamePlaceholder')}
                        value={newVaccine.otherName}
                        onChange={(e) => setNewVaccine({ ...newVaccine, otherName: e.target.value })}
                        required
                        className="form-input border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                    />
                </div>
            )}
            <div className="form-group">
                <label htmlFor="lastGivenDate" className="block text-dafauna-darkText font-medium mb-2">
                    {t('vaccine.lastGivenDate')}
                </label>
                <input
                    type="date"
                    id="lastGivenDate"
                    placeholder={t('vaccine.lastGivenDatePlaceholder')}
                    value={newVaccine.lastGivenDate}
                    onChange={(e) => setNewVaccine({ ...newVaccine, lastGivenDate: e.target.value })}
                    required
                    className="form-input border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                />
            </div>
            <div className="form-group">
                <label htmlFor="recurrence" className="block text-dafauna-darkText font-medium mb-2">
                    {t('vaccine.recurrence')}
                </label>
                <select
                    id="recurrence"
                    value={newVaccine.recurrence}
                    onChange={(e) => setNewVaccine({ ...newVaccine, recurrence: e.target.value })}
                    required
                    className="form-select border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                >
                    <option value="">{t('vaccine.recurrencePlaceholder')}</option>
                    {recurrenceOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label[i18n.language]} {/* Use multilingual labels */}
                        </option>
                    ))}
                </select>
            </div>
            <div className="form-group">
                <label htmlFor="endDate" className="block text-dafauna-darkText font-medium mb-2">
                    {t('vaccine.endDate')}
                </label>
                <input
                    type="date"
                    id="endDate"
                    placeholder={t('vaccine.endDatePlaceholder')}
                    value={newVaccine.endDate}
                    onChange={(e) => setNewVaccine({ ...newVaccine, endDate: e.target.value })}
                    required
                    className="form-input border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                />
            </div>
            <button
                onClick={handleAddVaccine}
                className="add-vaccine-page-btn w-full bg-dafauna-primary text-white rounded-md px-4 py-2 hover:bg-dafauna-primaryLight disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
                {t('vaccine.addButton')}
            </button>
        </div>
    );
};

export default AddVaccinePage;